import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import api from 'src/services/api';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { objectToQueryString } from 'src/utils/uri';

export const DEPLOYMENT_RINGS_QUERY_KEY = 'deploymentRings';
export const DEPLOYMENT_RING_DEVICES_QUERY_KEY = 'deploymentRingDevices';

const getDeploymentRings = ({ customerEnvId, signal }) =>
  api.get(`/deploymentRing/${customerEnvId}/rings`, {
    ...(signal ? { signal } : {}),
  });

const getDeploymentRingDevices = ({ customerEnvId, ringId, page, limit, query, signal }) =>
  api.get(`/deploymentRing/${customerEnvId}/ring/${ringId}/devices?${objectToQueryString(query)}`, {
    params: {
      Page: page,
      Limit: limit,
    },
    ...(signal ? { signal } : {}),
  });

const postDeploymentRing = ({ customerEnvId, payload }) =>
  api.post(`/deploymentRing/${customerEnvId}/ring`, payload);

const putDeploymentRing = ({ customerEnvId, ringId, payload }) =>
  api.put(`/deploymentRing/${customerEnvId}/ring/${ringId}`, payload);

const postActivateDeploymentRing = ({ customerEnvId, ringId }) =>
  api.post(`/deploymentRing/${customerEnvId}/ring/${ringId}/activate`);

const postSetDefaultDeploymentRing = ({ customerEnvId, ringId }) =>
  api.post(`/deploymentRing/${customerEnvId}/ring/${ringId}/setDefault`);

const deleteDeploymentRing = ({ customerEnvId, ringId }) =>
  api.delete(`/deploymentRing/${customerEnvId}/ring/${ringId}`);

// The list is the only thing a ring write can change, so one invalidation covers every mutation.
const invalidateDeploymentRingQueries = queryClient =>
  queryClient.invalidateQueries({ queryKey: [DEPLOYMENT_RINGS_QUERY_KEY], exact: false });

const useDeploymentRingsQuery = ({ enabled = true, select } = {}) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useQuery(
    [DEPLOYMENT_RINGS_QUERY_KEY, customerEnvId],
    ({ signal }) => getDeploymentRings({ customerEnvId, signal }),
    {
      enabled: enabled && !!customerEnvId,
      refetchOnWindowFocus: false,
      select,
    },
  );
};

export const useDeploymentRings = ({ enabled = true } = {}) => useDeploymentRingsQuery({ enabled });

// There is no single-ring endpoint, so a ring is picked out of the list query's cache entry - the
// list and every ring's details page share one fetch, and any write that refreshes the list refreshes
// this too.
export const useDeploymentRing = ({ ringId, enabled = true }) =>
  useDeploymentRingsQuery({
    enabled,
    select: data => data.rings?.find(ring => ring.id === Number(ringId)),
  });

// Same response shape as the devices page, so a ring's device list pages the same way.
export const useInfiniteDeploymentRingDevices = ({ ringId, apiQuery, limit = 50 }) => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);

  return useInfiniteQuery(
    [DEPLOYMENT_RING_DEVICES_QUERY_KEY, customerEnvId, ringId, apiQuery, limit],
    ({ pageParam = 1, signal }) =>
      getDeploymentRingDevices({
        customerEnvId,
        ringId,
        page: pageParam,
        limit,
        query: apiQuery,
        signal,
      }),
    {
      enabled: !!customerEnvId && !!ringId,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((acc, page) => acc + (page.devices?.length || 0), 0);
        return loaded < (lastPage.count || 0) ? pages.length + 1 : undefined;
      },
    },
  );
};

export const useAddDeploymentRing = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: payload => postDeploymentRing({ customerEnvId, payload }),
    onSuccess: () => invalidateDeploymentRingQueries(queryClient),
  });
};

export const useEditDeploymentRing = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ringId, payload }) => putDeploymentRing({ customerEnvId, ringId, payload }),
    onSuccess: () => invalidateDeploymentRingQueries(queryClient),
  });
};

export const useActivateDeploymentRing = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ringId }) => postActivateDeploymentRing({ customerEnvId, ringId }),
    onSuccess: () => invalidateDeploymentRingQueries(queryClient),
  });
};

export const useSetDefaultDeploymentRing = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ringId }) => postSetDefaultDeploymentRing({ customerEnvId, ringId }),
    onSuccess: () => invalidateDeploymentRingQueries(queryClient),
  });
};

export const useDeleteDeploymentRing = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ringId }) => deleteDeploymentRing({ customerEnvId, ringId }),
    onSuccess: () => invalidateDeploymentRingQueries(queryClient),
  });
};
